import { useEffect, useReducer, useState } from 'react';

import {
  Row,
  Col,
  Typography,
  Skeleton,
  notification,
  Tabs,
  Badge,
  Popconfirm,
  Modal,
  Descriptions,
  Form,
  Input,
  Alert,
  Space,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  EyeOutlined,
  MessageFilled,
  SelectOutlined,
} from '@ant-design/icons';

import CustomSelector from '../../components/CustomSelector';

import { CustomButton, IconButton } from '../../components';
import MESSAGES from '../../enums/Messages';
import { Link } from 'react-router-dom';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import BreadcrumbContainer from '../../containers/Breadcrumb';
import {
  getCounters,
  signMassive,
  signSelected,
  ajaxTable as filterAjaxTable,
  reject,
  sign,
  rejectMassive,
  rejectSelected,
  getGroups,
  getUsers,
  returnMassive,
  returnSelected,
  getProjects,
  getContractTypes,
  getRoles,
  getTerms,
} from '../../api/module/term_director_validation_document';
import AjaxTable from '../../components/AjaxTable';
import Paragraph from 'antd/lib/typography/Paragraph';
import { catchException } from '../../utils/helpers/helpers';

const { TextArea } = Input;

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const breadcrumbItems = [
  {
    text: 'Firma de TDR de Contratistas y Proveedores',
  },
];

const columns = [
  {
    title: 'Proyecto',
    dataIndex: 'proyecto_nombre',
  },
  {
    title: 'Identificador',
    dataIndex: 'identificador_interno',
  },
  {
    title: 'Tipo persona',
    dataIndex: 'tipo_persona_nombre',
  },
  {
    title: 'Nombre completo/Razón social',
    dataIndex: 'nombre_completo',
  },
  {
    title: 'Tipo doc',
    dataIndex: 'tipo_identificacion',
  },
  {
    title: 'Nro. Documento',
    dataIndex: 'numero_identificacion',
  },
  {
    title: 'Fecha inicio',
    dataIndex: 'fecha_inicio',
  },
  {
    title: 'Fecha fin',
    dataIndex: 'fecha_fin',
  },
];

const initModalState = {
  onFinish: async () => {},
  open: false,
  record: undefined,
  type: undefined,
};

const initSelectors = {
  proyecto: undefined,
  cargo: undefined,
  identificador: undefined,
};

function reducer(state, action) {
  switch (action.type) {
    case 'project':
      return { ...initSelectors, proyecto: action.payload };
    case 'ct':
      return {
        ...initSelectors,
        proyecto: state.proyecto,
        tipo_contrato: action.payload,
      };
    case 'role':
      return {
        ...state,
        cargo: action.payload,
        identificador: initSelectors.identificador,
      };
    case 'term':
      return {
        ...state,
        identificador: action.payload?.value,
        ii: action.payload?.label,
      };
    default:
      return initSelectors;
  }
}

function DocumentValidationDirectorSelectors({ match, setQuery }) {
  const [state, dispatch] = useReducer(reducer, initSelectors);

  useEffect(() => {
    if (!match.params.type || Object.values(state).some((value) => !!value)) {
      let q = '';
      if (q === '' && state.identificador) {
        q = `filter[termino]=${state.identificador}`;
      }
      if (q === '' && state.cargo) {
        q = `filter[cargo]=${state.cargo}&filter[tipo_contrato]=${state.tipo_contrato}`;
      }
      if (q === '' && state.tipo_contrato) {
        q = `filter[tipo_contrato]=${state.tipo_contrato}&filter[proyecto]=${state.proyecto}`;
      }
      if (q === '' && state.proyecto) {
        q = `filter[proyecto]=${state.proyecto}`;
      }
      setQuery({ query: q, param: state });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <div className="box-description" key="vdd-selectors">
      <Row className="box-selectors">
        <Col span={6}>
          <Title level={4}>Administración:</Title>
        </Col>
        <Col span={17} offset={0.5}>
          <CustomSelector
            selectorProps={{
              style: { width: '100%' },
              placeholder: 'Seleccione el proyecto a revisar...',
            }}
            valueName="id"
            labelName="nombre"
            endpointFetch={async () => {
              return { data: { results: [] } };
            }}
          />
        </Col>
      </Row>
      <Row className="box-selectors">
        <Col span={6}>
          <Title level={4}>Proyecto a revisar:</Title>
        </Col>
        <Col span={17} offset={0.5}>
          <CustomSelector
            onChange={(value) => dispatch({ type: 'project', payload: value })}
            initialValue={
              match.params.type && match.params.type === 'project'
                ? parseInt(match.params.id)
                : undefined
            }
            onLoad={(value) => {
              if (match.params.type === 'project')
                dispatch({
                  type: 'project',
                  payload: value ? value.value : undefined,
                });
            }}
            selectorProps={{
              style: { width: '100%' },
              placeholder: 'Seleccione el proyecto a revisar...',
            }}
            endpointFetch={async () => {
              return await getProjects();
            }}
            key={match.url}
          />
        </Col>
      </Row>
      {state.proyecto && (
        <Row className="box-selectors">
          <Col span={6}>
            <Title level={4}>Tipo de contrato:</Title>
          </Col>
          <Col span={17} offset={0.5}>
            <CustomSelector
              onChange={(value) => dispatch({ type: 'ct', payload: value })}
              initialValue={
                match.params.type && match.params.type === 'ct'
                  ? parseInt(match.params.id)
                  : undefined
              }
              onLoad={(value) => {
                if (match.params.type === 'ct')
                  dispatch({
                    type: 'ct',
                    payload: value ? value.value : undefined,
                  });
              }}
              selectorProps={{
                style: { width: '100%' },
                placeholder: 'Seleccione el tipo de contrato a revisar... ',
              }}
              endpointFetch={async () => {
                return await getContractTypes({
                  'filter[proyecto]': state.proyecto,
                });
              }}
              key={`rs-${state.proyecto}`}
            />
          </Col>
        </Row>
      )}
      {state.tipo_contrato && (
        <Row className="box-selectors">
          <Col span={6}>
            <Title level={4}>Cargo/rol a revisar:</Title>
          </Col>
          <Col span={17} offset={0.5}>
            <CustomSelector
              onChange={(value) => dispatch({ type: 'role', payload: value })}
              initialValue={
                match.params.type && match.params.type === 'role'
                  ? parseInt(match.params.id)
                  : undefined
              }
              onLoad={(value) => {
                if (match.params.type === 'role')
                  dispatch({
                    type: 'role',
                    payload: value ? value.value : undefined,
                  });
              }}
              selectorProps={{
                style: { width: '100%' },
                placeholder: 'Seleccione el Cargo / Rol a revisar... ',
              }}
              endpointFetch={async () => {
                return await getRoles({
                  'filter[proyecto]': state.proyecto,
                  'filter[tipo_contrato]': state.tipo_contrato,
                });
              }}
              key={`rs-${state.tipo_contrato}`}
            />
          </Col>
        </Row>
      )}
      {state.cargo && (
        <Row className="box-selectors" align="middle">
          <Col span={6}>
            <Title level={4} style={{ paddingRight: '15px' }}>
              Identificador del término a revisar:
            </Title>
          </Col>
          <Col span={17} offset={0.5}>
            <CustomSelector
              onChange={(value) => dispatch({ type: 'term', payload: value })}
              initialValue={
                match.params.type && match.params.type === 'term'
                  ? parseInt(match.params.id)
                  : undefined
              }
              onLoad={(value) => {
                if (match.params.type === 'term')
                  dispatch({
                    type: 'term',
                    payload: value
                      ? { value: value.value, label: value.label }
                      : undefined,
                  });
              }}
              selectorProps={{
                style: { width: '100%' },
                placeholder:
                  'Seleccione el identificador único del termino de referencia... ',
                labelInValue: true,
              }}
              endpointFetch={async () => {
                return await getTerms({
                  'filter[cargo]': state.cargo,
                  'filter[tipo_contrato]': state.tipo_contrato,
                });
              }}
              key={`rs-${state.cargo}`}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}

function MultipleReturnModal({
  termID,
  ii,
  onFinish,
  onCancel,
  asGroup = false,
  error = undefined,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  async function onSelfFinish(values) {
    setLoading(true);
    await onFinish(values);
    setLoading();
  }

  if (!termID) return null;

  return (
    <Modal
      centered
      okText="Enviar"
      cancelText="volver"
      title={
        <Title level={4}>
          Devolución {asGroup ? 'Masiva' : 'Seleccionados'}
        </Title>
      }
      closable={false}
      open={true}
      footer={
        <>
          <CustomButton
            text="Cancelar"
            type="default"
            htmlType="button"
            onClick={onCancel}
          />
          <CustomButton
            text="Devolver"
            type="primary"
            className="background-bigaro"
            htmlType="button"
            loading={loading}
            onClick={() => {
              form.submit();
            }}
          />
        </>
      }
    >
      <p>
        TDR seleccionado: <b>{ii}</b>.
      </p>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSelfFinish}
        initialValues={{ identificador_interno: ii }}
        onValuesChange={(cv, _v) => {
          if (cv.hasOwnProperty('rol')) {
            form.setFieldsValue({
              usuario_destino: undefined,
            });
          }
        }}
      >
        <Form.Item
          label="Selección del rol que revisará"
          name="rol"
          rules={[{ required: true }]}
        >
          <CustomSelector
            endpointFetch={async () => {
              return await getGroups({
                termino: termID,
              });
            }}
            key={`rs_${termID}`}
          />
        </Form.Item>
        <Form.Item noStyle dependencies={['rol']}>
          {({ getFieldValue }) => {
            const group = getFieldValue('rol');
            return (
              <Form.Item
                label="Selección de la persona que revisará"
                name="usuario_destino"
                rules={[{ required: true }]}
              >
                <CustomSelector
                  endpointFetch={async () => {
                    if (!group) return { data: { results: [] } };
                    return await getUsers({
                      termino: termID,
                      grupo: group,
                    });
                  }}
                  disabled={!group}
                  key={`rs_${termID}_${group}`}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item
          label="Nuevo Identificador Interno de TDR"
          name="identificador_interno"
          rules={[{ required: true }]}
        >
          <TextArea
            placeholder="Digite aquí"
            maxLength={80}
            showCount
            autoSize={{ minRows: 1, maxRows: 1 }}
          />
        </Form.Item>
        <Form.Item
          label="Justifique la devolución masiva de este TDR"
          name="observacion"
          rules={[{ required: true }]}
        >
          <TextArea />
        </Form.Item>
      </Form>
      {error && (
        <Alert type="error" message="Error" description={error} showIcon />
      )}
    </Modal>
  );
}

function RejectModal({ record, open, onFinish, onCancel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  async function onSelfFinish(values) {
    setLoading(record.id);
    await onFinish(values);
    setLoading();
  }

  if (!record) return null;

  return (
    <Modal
      centered
      title={<Title level={4}>Rechazo Individual</Title>}
      closable={false}
      open={open}
      footer={
        <>
          <CustomButton
            type="default"
            htmlType="button"
            text="Cancelar"
            onClick={onCancel}
          />
          <CustomButton
            type="danger"
            text="Rechazar"
            htmlType="button"
            loading={loading === record.id}
            onClick={() => {
              form.submit();
            }}
          />
        </>
      }
    >
      {record.identificador_interno && (
        <p>
          Este registro pertenece al termino:{' '}
          <b>{record.identificador_interno}</b>.
        </p>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onSelfFinish}
        initialValues={{ observacion: record.observaciones_director }}
      >
        <Form.Item
          label="Justifique el rechazo individual de este contrato"
          name="observacion"
          rules={[{ required: true, message: 'Ingrese una justificación' }]}
        >
          <TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function MultipleRejectModal({ open, onFinish, onCancel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  async function onSelfFinish(values) {
    setLoading(true);
    await onFinish(values);
    setLoading();
  }

  return (
    <Modal
      centered
      title={<Title level={4}>Rechazo masivo</Title>}
      closable={false}
      open={open}
      footer={
        <>
          <CustomButton
            type="default"
            htmlType="button"
            text="Cancelar"
            onClick={onCancel}
          />
          <CustomButton
            type="danger"
            text="Rechazar"
            htmlType="button"
            loading={loading}
            onClick={() => {
              form.submit();
            }}
          />
        </>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSelfFinish}>
        <Form.Item
          label="Justifique el rechazo masivo de estos contratos"
          name="observacion"
          rules={[{ required: true, message: 'Ingrese una justificación' }]}
        >
          <TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function DocumentValidationDirectorHome({ match }) {
  const [query, setQuery] = useState({ q: '', param: {} });
  const [counters, setCounters] = useState(undefined);
  const [selected, setSelected] = useState([]);

  const [activeTab, setActiveTab] = useState('review');
  const [massiveLoad, setMassiveLoad] = useState(false);

  const [modalState, setModalState] = useState(initModalState);

  const [loadingIDs, setLoadingIDs] = useState([]);

  const [reloadReview, setReloadReview] = useState(false);

  const [actionType, setActionType] = useState(undefined);
  const [mrError, setMRError] = useState(undefined);

  async function onMassiveSign() {
    async function onOk() {
      setMassiveLoad(true);
      setActionType('sign');
      try {
        const res = await signMassive({
          ...query.param,
          ii: null,
          identificador: null,
          termino: query.param.identificador,
        });
        if (res.data.result === false)
          throw new Error('La firma masiva no resulto correctamente');
        changeQuery(query, 'approved');
      } catch (err) {
        console.log(err);
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_POST_MSG,
          duration: 2,
        });
      }
      setMassiveLoad(false);
      setActionType(undefined);
    }

    if (counters.pending > 0) {
      Modal.confirm({
        title: `Se firmarán ${counters.pending} registros.`,
        content: '¿Está seguro(a)?',
        onOk,
        cancelText: 'No',
        okText: 'Sí',
      });
    }
  }

  function onSelectedSign() {
    async function onOk() {
      setActionType('sign');
      const data = {
        validaciones: selected,
        proyecto: query.param.proyecto,
      };
      setLoadingIDs(data.validaciones);
      try {
        const res = await signSelected(data, {
          ...query.param,
          ii: null,
          identificador: null,
          termino: query.param.identificador,
        });
        if (res.data.result === false)
          throw new Error('La firma masiva no resulto correctamente');
        changeQuery(query, 'approved');
      } catch (err) {
        console.log(err);
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_POST_MSG,
          duration: 2,
        });
      }
      setLoadingIDs([]);
      setActionType(undefined);
    }

    if (selected.length > 0) {
      Modal.confirm({
        title: `Se firmarán ${selected.length} registros.`,
        content: '¿Está seguro(a)?',
        onOk,
        cancelText: 'No',
        okText: 'Sí',
      });
    }
  }

  function onMassiveReject() {
    async function onOk(values) {
      setMassiveLoad(true);
      try {
        const res = await rejectMassive(values, {
          ...query.param,
          ii: null,
          identificador: null,
          termino: query.param.identificador,
        });
        if (res.data.result === false)
          throw new Error('El rechazo masivo no resulto correctamente');
        changeQuery(query, 'rejected');
      } catch (err) {
        console.log(err);
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_POST_MSG,
          duration: 2,
        });
      }
      setMassiveLoad(false);
      setActionType(undefined);
      setModalState(initModalState);
    }

    if (counters.pending > 0) {
      Modal.confirm({
        title: `Se rechazarán ${counters.pending} registros.`,
        content: '¿Está seguro(a)?',
        onOk: () => {
          setActionType('reject');
          setModalState({
            type: 'm_reject',
            open: true,
            onFinish: onOk,
          });
        },
        cancelText: 'No',
        okText: 'Sí',
      });
    }
  }

  function onSelectedReject() {
    async function onOk(values) {
      const data = {
        ...values,
        validaciones: selected,
        proyecto: query.param.proyecto,
      };
      setLoadingIDs(data.validaciones);
      setActionType('reject');
      try {
        const res = await rejectSelected(data, {
          ...query.param,
          ii: null,
          identificador: null,
          termino: query.param.identificador,
        });
        if (res.data.result === false)
          throw new Error('El rechazo masivo no resulto correctamente');
        changeQuery(query, 'rejected');
      } catch (err) {
        console.log(err);
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_POST_MSG,
          duration: 2,
        });
      }
      setLoadingIDs([]);
      setActionType(undefined);
    }

    if (selected.length > 0) {
      Modal.confirm({
        title: `Se rechazarán ${selected.length} registros.`,
        content: '¿Está seguro(a)?',
        onOk: () => {
          setActionType('reject');
          setModalState({
            type: 'm_reject',
            open: true,
            onFinish: onOk,
          });
        },
        cancelText: 'No',
        okText: 'Sí',
      });
    }
  }

  async function changeQuery(
    q = { query: '', param: undefined },
    newTab = undefined,
  ) {
    try {
      const res = await getCounters(`/?${q.query}`);
      if (q !== query) setQuery(q);
      setCounters(res.data.results);
      if (newTab) setActiveTab(newTab);
      setSelected([]);
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_GET_MSG,
        duration: 2,
      });
      setCounters(undefined);
    }
  }

  async function reloadCounters() {
    const res = await getCounters(`/?${query.query}`);
    setCounters(res.data.results);
    setSelected([]);
  }

  async function onSave(record) {
    try {
      setLoadingIDs([record.id]);
      await sign(record.id);
      setLoadingIDs([]);
      reloadCounters();
      setActiveTab('approved');
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
    }
  }

  async function onRejectedStatusSave(id, text) {
    try {
      await reject(id, {
        observacion: text,
      });
      reloadCounters();
      setActiveTab('review');
      setReloadReview({});
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
    }
  }

  async function onMassiveReturn(values) {
    setMassiveLoad(true);
    try {
      await returnMassive(query.param.identificador, values);
      reloadCounters();
      setReloadReview({});
      setActionType(undefined);
      setMRError(undefined);
    } catch (err) {
      setMRError(catchException(err));
    }
    setMassiveLoad(false);
  }

  async function onSelectedReturn(values) {
    const data = {
      ...values,
      validaciones: selected,
    };
    setLoadingIDs(data.validaciones);
    setActionType('s_return');
    try {
      await returnSelected(query.param.identificador, data);
      reloadCounters();
      setReloadReview({});
      setActionType(undefined);
      setMRError(undefined);
    } catch (err) {
      setMRError(catchException(err));
    }
    setLoadingIDs([]);
  }

  const rowSelectionToCheck = {
    selectedRowKeys: selected,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelected(selectedRowKeys);
    },
  };

  const extraColumnsToCheck = [
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        return (
          <Space>
            <IconButton
              bordered
              title="Ver Documentos"
              color="blue"
              icon={
                <Link
                  to={`${INTERNAL_LINKS.DOCUMENT_VALIDATION_DIRECTOR}-validar/${record.id}`}
                >
                  <EyeOutlined />
                </Link>
              }
            />
            <Popconfirm
              onConfirm={() => {
                onSave(record);
              }}
              title={MESSAGES.MSG_SAVE_STATE}
            >
              <IconButton
                bordered
                title="Firmar"
                loading={loadingIDs.includes(record.id)}
                disabled={loadingIDs.length > 0}
                color="green"
                icon={<CheckCircleFilled />}
              />
            </Popconfirm>
            <Popconfirm
              onConfirm={() => {
                setModalState({
                  type: 'i_reject',
                  open: true,
                  onFinish: async (values) => {
                    await onRejectedStatusSave(record.id, values);
                    setModalState(initModalState);
                  },
                  record: record,
                });
              }}
              title={MESSAGES.MSG_SAVE_STATE}
            >
              <IconButton
                bordered
                title="Rechazar"
                disabled={loadingIDs.length > 0}
                color="red"
                icon={<CloseCircleFilled />}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const extraColumnsApproved = [
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        return (
          <IconButton
            bordered
            title="Ver Documentos"
            color="blue"
            icon={
              <Link
                to={`${INTERNAL_LINKS.DOCUMENT_VALIDATION_DIRECTOR}-validar/${record.id}`}
              >
                <EyeOutlined />
              </Link>
            }
          />
        );
      },
    },
  ];

  const extraColumnsRejected = [
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        return (
          <IconButton
            bordered
            title="Motivo"
            color="lime"
            icon={<MessageFilled />}
            onClick={() => {
              Modal.info({
                title: 'Motivo Rechazo',
                centered: true,
                content: (
                  <Input.TextArea
                    rows={4}
                    value={record.observacion_director}
                    disabled
                  />
                ),
              });
            }}
          />
        );
      },
    },
  ];

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Firma de TDR de Contratistas y Proveedores
      </Title>
      <DocumentValidationDirectorSelectors
        match={match}
        setQuery={changeQuery}
      />

      {counters ? (
        <>
          <Descriptions
            bordered
            labelStyle={{ display: 'block', textAlign: 'center' }}
            contentStyle={{ display: 'block', textAlign: 'center' }}
            style={{ marginTop: '15px' }}
            layout="vertical"
            column={2}
          >
            <Descriptions.Item
              style={{ width: '50%' }}
              label={
                <CustomButton
                  type="default"
                  size="large"
                  icon={<EditOutlined />}
                  text={
                    <>
                      Firmar masivos{' '}
                      <Badge
                        count={counters.pending}
                        className="site-badge-count-4"
                        showZero={true}
                      />
                    </>
                  }
                  htmlType="button"
                  onClick={onMassiveSign}
                  loading={massiveLoad}
                  disabled={
                    counters.pending < 1 ||
                    activeTab !== 'review' ||
                    !(
                      query.param.proyecto ||
                      query.param.cargo ||
                      query.param.tipo_contrato ||
                      query.param.identificador
                    )
                  }
                />
              }
            >
              <Text>
                Para habilitar <Text strong>Firmar masivos</Text> por favor
                seleccione los filtros de Proyecto, Tipo de contrato, Cargo/Rol
                o Identificador del término a revisar. El número indica los
                registros que serán firmados.
              </Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <CustomButton
                  type="default"
                  size="large"
                  icon={<SelectOutlined />}
                  text={
                    <>
                      Firmar seleccionados{' '}
                      <Badge
                        count={selected.length}
                        className="site-badge-count-4"
                        showZero={true}
                      />
                    </>
                  }
                  htmlType="button"
                  onClick={onSelectedSign}
                  loading={massiveLoad || loadingIDs.length > 0}
                  disabled={selected.length < 1 || activeTab !== 'review'}
                />
              }
            >
              <Text>
                Para habilitar <Text strong>Firmar seleccionados</Text>, por
                favor seleccione los registros deseados de la tabla a
                continuación. El número indica los registros que serán firmados.
              </Text>
            </Descriptions.Item>
          </Descriptions>
          <Descriptions
            bordered
            labelStyle={{ display: 'block', textAlign: 'center' }}
            contentStyle={{ display: 'block', textAlign: 'center' }}
            style={{ marginTop: '15px' }}
            layout="vertical"
            column={2}
          >
            <Descriptions.Item
              style={{ width: '50%' }}
              label={
                <CustomButton
                  type="danger"
                  size="large"
                  icon={<EditOutlined />}
                  text={
                    <>
                      Rechazo masivo{' '}
                      <Badge
                        count={counters.pending}
                        className="site-badge-count-4"
                        showZero={true}
                      />
                    </>
                  }
                  htmlType="button"
                  onClick={onMassiveReject}
                  loading={actionType === 'reject' && massiveLoad}
                  disabled={
                    actionType ||
                    counters.pending < 1 ||
                    activeTab !== 'review' ||
                    !(
                      query.param.proyecto &&
                      query.param.tipo_contrato &&
                      query.param.cargo &&
                      query.param.identificador
                    )
                  }
                />
              }
            >
              <Text>
                Para habilitar <Text strong>Rechazo masivo</Text> por favor
                seleccione los filtros de Proyecto, Tipo de contrato, Cargo/Rol,
                e Identificador del término a revisar. El número indica los
                registros que serán rechazados.
              </Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <CustomButton
                  type="danger"
                  size="large"
                  icon={<SelectOutlined />}
                  text={
                    <>
                      Rechazo seleccionados{' '}
                      <Badge
                        count={selected.length}
                        className="site-badge-count-4"
                        showZero={true}
                      />
                    </>
                  }
                  htmlType="button"
                  onClick={onSelectedReject}
                  loading={
                    actionType === 'reject' &&
                    (massiveLoad || loadingIDs.length > 0)
                  }
                  disabled={
                    actionType || selected.length < 2 || activeTab !== 'review'
                  }
                />
              }
            >
              <Text>
                Para habilitar <Text strong>Rechazo seleccionados</Text>, por
                favor seleccione los registros deseados de la tabla a
                continuación. El número indica los registros que serán
                rechazados.
              </Text>
            </Descriptions.Item>
          </Descriptions>
          <Descriptions
            bordered
            labelStyle={{ display: 'block', textAlign: 'center' }}
            contentStyle={{ display: 'block', textAlign: 'center' }}
            style={{ marginTop: '15px' }}
            layout="vertical"
            column={2}
          >
            <Descriptions.Item
              style={{ width: '50%' }}
              label={
                <CustomButton
                  type="default"
                  size="large"
                  icon={<EditOutlined />}
                  text={
                    <>
                      Devolución Masiva{' '}
                      <Badge
                        count={counters.pending}
                        className="site-badge-count-4"
                        showZero={true}
                      />
                    </>
                  }
                  htmlType="button"
                  onClick={() => {
                    if (counters.pending > 0) {
                      Modal.confirm({
                        title: `Se devolverán ${counters.pending} registros.`,
                        content: '¿Está seguro(a)?',
                        onOk: () => {
                          setActionType('m_return');
                        },
                        cancelText: 'No',
                        okText: 'Sí',
                      });
                    }
                  }}
                  loading={actionType === 'm_return' && massiveLoad}
                  disabled={
                    actionType ||
                    activeTab !== 'review' ||
                    !(
                      query.param.proyecto &&
                      query.param.tipo_contrato &&
                      query.param.cargo &&
                      query.param.identificador
                    )
                  }
                  className="background-bigaro"
                />
              }
            >
              <Text>
                Para habilitar <Text strong>Devolución masiva</Text> por favor
                seleccione los filtros de Proyecto, Tipo de contrato, Cargo/Rol
                e Identificador del término a revisar. El número indica los
                registros que serán devueltos.
                <br />
                <Text strong>Nota:</Text> En caso de que no todos los registros
                del TDR esten disponibles para devolución, esta opción hará que
                el(los) registro(s) disponibles se devuelva(n) y convierta(n) en
                un nuevo TDR.
              </Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <CustomButton
                  type="default"
                  size="large"
                  icon={<SelectOutlined />}
                  text={
                    <>
                      Devolución seleccionados{' '}
                      <Badge
                        count={selected.length}
                        className="site-badge-count-4"
                        showZero={true}
                      />
                    </>
                  }
                  htmlType="button"
                  onClick={() => {
                    if (selected.length > 0) {
                      Modal.confirm({
                        title: `Se devolverán ${selected.length} registros.`,
                        content: '¿Está seguro(a)?',
                        onOk: () => {
                          setActionType('s_return');
                        },
                        cancelText: 'No',
                        okText: 'Sí',
                      });
                    }
                  }}
                  loading={
                    actionType === 's_return' &&
                    (massiveLoad || loadingIDs.length > 0)
                  }
                  disabled={
                    actionType ||
                    selected.length < 1 ||
                    activeTab !== 'review' ||
                    !(
                      query.param.proyecto &&
                      query.param.tipo_contrato &&
                      query.param.cargo &&
                      query.param.identificador
                    )
                  }
                  className="background-bigaro"
                />
              }
            >
              <Text>
                Para habilitar <Text strong>Devolución seleccionados</Text>, por
                favor seleccione los filtros de Proyecto, Tipo de contrato,
                Cargo/Rol e Identificador del término a revisar. Luego,
                seleccione los registros deseados de la tabla a continuación. El
                número indica los registros que serán devueltos.
                <br />
                <Text strong>Nota:</Text> Esta opción hará que el(los)
                registro(s) se devuelva(n) y convierta(n) en un nuevo TDR.
              </Text>
            </Descriptions.Item>
          </Descriptions>
          {massiveLoad ? (
            <Skeleton active loading={true} />
          ) : (
            <Tabs
              style={{ marginTop: 15 }}
              defaultActiveKey={'review'}
              activeKey={activeTab}
              onTabClick={(key, _) => {
                setActiveTab(key);
              }}
              destroyInactiveTabPane={true}
              type="card"
              key={`vdd-home-tabs-${query.query}`}
            >
              <TabPane
                tab={
                  <Paragraph>
                    Por revisar
                    <Badge showZero count={counters.pending} />
                  </Paragraph>
                }
                key="review"
              >
                <AjaxTable
                  columns={[...columns, ...extraColumnsToCheck]}
                  tableTitle={'Listado de contratistas para revisar'}
                  endpoint={filterAjaxTable}
                  query={`/?${query.query}&filter[estado_director__isnull]=True`}
                  rowSelection={rowSelectionToCheck}
                  reloadSource={reloadReview}
                />
              </TabPane>
              <TabPane
                tab={
                  <Paragraph>
                    Aprobados
                    <Badge showZero count={counters.approved} />
                  </Paragraph>
                }
                key="approved"
              >
                <AjaxTable
                  columns={[...columns, ...extraColumnsApproved]}
                  tableTitle={'Listado de contratistas aprobados'}
                  endpoint={filterAjaxTable}
                  query={`/?${query.query}&filter[estado_director__sigla]=TDR-VDD-G-A`}
                />
              </TabPane>
              <TabPane
                tab={
                  <Paragraph>
                    Rechazados
                    <Badge showZero count={counters.rejected} />
                  </Paragraph>
                }
                key="rejected"
              >
                <AjaxTable
                  columns={[...columns, ...extraColumnsRejected]}
                  tableTitle={'Listado de contratistas rechazados'}
                  endpoint={filterAjaxTable}
                  query={`/?${query.query}&filter[estado_director__sigla]=TDR-VDD-G-R`}
                  searchInput
                />
              </TabPane>
            </Tabs>
          )}
        </>
      ) : (
        <Skeleton active loading={true} />
      )}
      {modalState?.type === 'i_reject' && (
        <RejectModal
          open={modalState.open}
          onFinish={modalState.onFinish}
          onCancel={() => {
            setModalState(initModalState);
          }}
          record={modalState.record}
          key={modalState?.record?.id || 'no-key'}
        />
      )}
      {modalState?.type === 'm_reject' && (
        <MultipleRejectModal
          open={modalState.open}
          onFinish={modalState.onFinish}
          onCancel={() => {
            setModalState(initModalState);
            setActionType(undefined);
          }}
        />
      )}
      {actionType === 'm_return' && (
        <MultipleReturnModal
          onFinish={onMassiveReturn}
          onCancel={() => {
            setActionType(undefined);
            setMRError(undefined);
          }}
          asGroup={true}
          termID={query.param.identificador}
          ii={query.param.ii}
          error={mrError}
        />
      )}
      {actionType === 's_return' && (
        <MultipleReturnModal
          onFinish={onSelectedReturn}
          onCancel={() => {
            setActionType(undefined);
            setMRError(undefined);
          }}
          termID={query.param.identificador}
          ii={query.param.ii}
          error={mrError}
        />
      )}
    </>
  );
}
